import { IBasketTotals } from "./interfaces/IBasketTotals";
import { IUpdatedDeliveryResult } from "./interfaces/IUpdatedDeliveryResult";

/**
 * The signature of the method that is called when the user selects a different delivery method in the UI.
 * Registered via the constructor.
 * This happens before the updated totals are requested from the server.
 */
export interface IDeliveryTypeSelectedInUiCallback {
    (deliveryTypeIsStore: boolean): void;
}

/**
 * The signature of the method that is called when the server returns updated order totals after a user has changed their
 * delivery details (address or delivery type).
 * Registered via the constructor.
 */
export interface IOrderTotalsUpdatedCallback {
    (updated: IUpdatedDeliveryResult): void;
}

interface IDeliveryUpdate {
    billingCurrency: string;

    countryCode3: string;

    deliveryTypeId: string;

    postalCode: string;

    storeId: string;
}

export class DeliveryUpdate {
    public static Init(deliveryTypeSelectedInUiCallback?: IDeliveryTypeSelectedInUiCallback,
        orderTotalsUpdatedCallback?: IOrderTotalsUpdatedCallback): DeliveryUpdate {
        return new DeliveryUpdate(deliveryTypeSelectedInUiCallback, orderTotalsUpdatedCallback);
    }

    private readonly deliveryDropdownId = "DeliveryServices_DeliveryStoreId";
    public readonly preventDeliveryRefresh = this.fieldValueAsBoolean("PreventDeliveryRefresh");

    constructor(private deliveryTypeSelectedInUiCallback?: IDeliveryTypeSelectedInUiCallback,
        private orderTotalsUpdatedCallback?: IOrderTotalsUpdatedCallback) {
        this.WireUpDeliveryServiceSelection();
        this.WireUpStoreChange();
    }

    private WireUpDeliveryServiceSelection(): void {
        let radios = document.getElementsByClassName("delivery-radio");

        for (const element of radios) {
            element.addEventListener("change", (e) => this.onDeliveryTypeChanged(e));
        }
    }

    private async onDeliveryTypeChanged(e: Event): Promise<void> {
        e.preventDefault();

        if (this.preventDeliveryRefresh) {
            window.Modal.CloseModal();
            this.updateDutyAndTaxTotals();
        }
        else {
            this.RequestDeliveryUpdate();
        }
    }

    private updateDutyAndTaxTotals(): void {
        let element = this.selectedDeliveryHtmlElement();

        if (element) {
            const updates = {
                charge: parseFloat(element.dataset.charge),
                duty: parseFloat(element.dataset.duty),
                salesTax: parseFloat(element.dataset.salesTax),
                courierFee: parseFloat(element.dataset.courierFee)
            };
            // Update delivery total and order total by using the charge data from the selected delivery type radio button.
            this.patchBasketTotals(updates);

            // Pass the updated totals to the UI component updaters.
            let updatedDelivery: IUpdatedDeliveryResult = {
                deliveryServicesAsJson: "",
                deliveryServicesHtml: "",
                deliveryTypes: [],
                storeAddress: [],
                storeId: "",
                totals: this.basketTotals,
            };

            this.updateOrderSummaryInternal(updatedDelivery);
        }
        else {
            window.IssueLogger.log("No delivery option selected. Should not be possible.", "DeliveryUpdate.onDeliveryTypeChanged");
        }
    }

    /**
     * Updates the in-memory representation of the basket totals with the passed values.
     * Avoids having to go back to the server to get the updated totals.
     * @param updates
     */
    private patchBasketTotals(updates: { charge: number; duty: number; salesTax: number; courierFee: number; }): void {
        const totals = this.basketTotals;

        totals.delivery = updates.charge;
        totals.deliveryAsDecimal = updates.charge.toFixed(2);
        totals.deliveryTotalFormatted = totals.currencySymbol + updates.charge.toFixed(2);

        totals.dutyTotalFormatted = totals.currencySymbol + updates.duty.toFixed(2);
        totals.salesTaxAmountFormatted = totals.currencySymbol + updates.salesTax.toFixed(2);
        totals.courierFeeFormatted = totals.currencySymbol + updates.courierFee.toFixed(2);

        const total = totals.basketItemTotal + totals.delivery;
        totals.totalFormatted = totals.currencySymbol + total.toFixed(2);

        this.basketTotals = totals;
    }

    public async RequestDeliveryUpdate(countryCode3?: string, postalCode?: string): Promise<void> {
        let postData = this.formToDeliveryUpdate(countryCode3, postalCode);

        this.UpdateUiForDeliveryType();

        if (this.deliveryTypeSelectedInUiCallback) {
            this.deliveryTypeSelectedInUiCallback(this.selectedDeliveryTypeIsStore());
        }

        let modalDivElement = <HTMLDivElement>document.getElementById("delivery-update-modal");
        if (modalDivElement) {
            // Don't show modal if country is UK as the lookup is very fast there.
            if (this.selectedDeliveryCountryCode() !== "GBR") {
                window.Modal.OpenModal(modalDivElement, () => { });
            }
        }

        try {
            const response = await fetch("/ws/update-delivery", {
                body: JSON.stringify(postData),
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
            });

            // Check if the response is not ok
            if (!response.ok) {
                // We can't ship to this location, a message will have been set via TempData for display in the basket.
                window.location.href = "/your-basket";
                return;
            }

            window.Modal.CloseModal();
            const data: IUpdatedDeliveryResult = await response.json();
            this.updateOrderSummary(data);
        } catch (error) {
            window.IssueLogger.log("DeliveryUpdate", "RequestDeliveryUpdate", error);
            // Handle any unexpected errors (e.g., network issues)
            window.location.href = "/your-basket";
        }
    }

    public UpdateUiForDeliveryType(): void {
        let addressContainerStore = document.getElementById("address-container-store");
        let deliveryStoresContainer = document.getElementById("delivery-stores-container");

        if (this.selectedDeliveryTypeIsStore()) {
            if (addressContainerStore) {
                addressContainerStore.classList.remove("hidden");
            }
            if (deliveryStoresContainer) {
                deliveryStoresContainer.classList.remove("hidden");
            }
        }
        else {
            if (addressContainerStore) {
                addressContainerStore.classList.add("hidden");
            }
            if (deliveryStoresContainer) {
                deliveryStoresContainer.classList.add("hidden");
            }
        }
    }

    private fieldValue(id: string): string {
        let element = (<HTMLInputElement>document.getElementById(id));

        if (!element) {
            return "";
        }

        return element.value;
    }

    private fieldValueAsBoolean(id: string): boolean {
        let element = (<HTMLInputElement>document.getElementById(id));

        if (!element) {
            return false;
        }

        return element.value.toLowerCase() === "true";
    }

    private formToDeliveryUpdate(countryCode3?: string, postalCode?: string): IDeliveryUpdate {

        // If no overrides passed (only done on the order-summary page for delivery recalculation) then use the form values.
        if (!countryCode3) {
            countryCode3 = this.selectedDeliveryCountryCode();
        }
        if (!postalCode) {
            postalCode = this.fieldValue("AddressPostalCode");
        }

        return {
            billingCurrency: this.selectedBillingCurrency(),
            countryCode3: countryCode3,
            deliveryTypeId: this.selectedDeliveryType,
            postalCode: postalCode,
            storeId: this.selectedDeliveryStore(),
        };
    }

    public selectedDeliveryTypeIsStore(): boolean {
        return this.selectedDeliveryType === "Store";
    }

    private selectedDeliveryStore(): string {
        return this.fieldValue(this.deliveryDropdownId);
    }

    public selectedDeliveryHtmlElement(): HTMLInputElement {
        return <HTMLInputElement>document.querySelector('input[name = "DeliveryServices.SelectedDeliveryType"]:checked');
    }

    //private get basketTotals(): IBasketTotals {
    //    return window["BasketTotals"];
    //}

    //private set basketTotals(basketTotals: IBasketTotals) {
    //    window["BasketTotals"] = basketTotals;
    //}

    public get selectedDeliveryType(): string {
        let selectedDeliveryRadio = this.selectedDeliveryHtmlElement();
        if (selectedDeliveryRadio) {
            return selectedDeliveryRadio.value;
        }
        return "0"; // Equivalent of NotSet enum, allows us to post to the server.
    }

    public set selectedDeliveryType(deliveryType: string) {
        let targetRadioButton = <HTMLElement>document.querySelector('input[name = "DeliveryServices.SelectedDeliveryType"][value="' + deliveryType + '"]');
        if (targetRadioButton) {
            targetRadioButton.click();
        }
    }

    private selectedBillingCurrency(): string {
        let element = <HTMLSelectElement>document.getElementById("BillingCurrency");

        if (!element || element.selectedIndex < 0) {
            return "";
        }

        return element.options[element.selectedIndex].value;
    }

    private selectedDeliveryCountryCode(): string {
        let countryElement = <HTMLSelectElement>document.getElementById("AddressCountryCode3");

        if (!countryElement || countryElement.selectedIndex < 0) {
            return "";
        }

        return countryElement.options[countryElement.selectedIndex].value;
    }

    private updateOrderSummary(updatedDelivery: IUpdatedDeliveryResult): void {
        if (!updatedDelivery) {
            return;
        }

        this.basketTotals = updatedDelivery.totals;

        // Patch the delivery services JSON which is loaded into the Apple Pay
        // component when it is reset. The delivery services JSON holds updated services
        // with the selected one always the first in the list.
        let deliveryServicesAsJsonElement = <HTMLInputElement>document.getElementById("DeliveryServicesAsJson");
        if (deliveryServicesAsJsonElement) {
            deliveryServicesAsJsonElement.value = updatedDelivery.deliveryServicesAsJson;
        }

        this.ReplaceDeliveryOptions(updatedDelivery.deliveryServicesHtml);

        this.updateOrderSummaryInternal(updatedDelivery);
    }

    private updateOrderSummaryInternal(updatedDelivery: IUpdatedDeliveryResult): void {
        this.UpdateOrderTotals(updatedDelivery.totals);
        this.UpdateUiForDeliveryType(); // Delivery type may have been changed by server (for example, store deselected if not in UK).

        if (this.orderTotalsUpdatedCallback) {
            this.orderTotalsUpdatedCallback(updatedDelivery);
        }
    }

    public ReplaceDeliveryOptions(html: string): void {
        let container = <HTMLDivElement>document.getElementById("delivery-options-container");

        if (!container) {
            return;
        }

        container.innerHTML = html;

        this.WireUpDeliveryServiceSelection();
        this.WireUpStoreChange();
    }

    public UpdateOrderTotals(basketTotals: IBasketTotals): void {
        document.querySelector(".basket-item-total").textContent = basketTotals.basketItemTotalFormattedWithoutDiscount;
        document.querySelector(".basket-delivery-total").textContent = basketTotals.deliveryTotalFormatted;
        document.getElementById("voucher-saving-text").textContent = basketTotals.voucherSavingText;
        this.setText("#sale-items-saving", basketTotals.saleItemsSavingText);
        this.setText(".basket-duty-total", basketTotals.dutyTotalFormatted);
        this.setText(".basket-courier-fee", basketTotals.courierFeeFormatted);
        document.getElementById("OrderVatLabel").textContent = basketTotals.salesTaxAmountFormatted;

        document.getElementById("OrderTotalLabel").textContent = basketTotals.totalFormatted;

        if (basketTotals.showSalesTaxAmountFormatted) {
            document.getElementById("vat-container").classList.remove("hidden");
        }
        else {
            document.getElementById("vat-container").classList.add("hidden");
        }

        let importWarningElement = document.getElementById("import-warning-container");
        if (basketTotals.showImportWarning) {
            importWarningElement.classList.remove("hidden");
        }
        else {
            importWarningElement.classList.add("hidden");
        }
        this.setText("#import-warning-message", basketTotals.importMessage);

        let iossElements = document.getElementsByClassName("ioss-element");

        for (const iossElement of iossElements) {
            if (basketTotals.showDuty) {
                iossElement.classList.remove("hidden");
            }
            else {
                iossElement.classList.add("hidden");
            }
        }

        let courierFeeContainer = document.getElementById("courier-fee-container");
        if (basketTotals.showCourierFee) {
            courierFeeContainer.classList.remove("hidden");
        }
        else {
            courierFeeContainer.classList.add("hidden");
        }

        if (basketTotals.showVoucherSaving) {
            document.getElementById("VoucherSavingContainer").classList.remove("hidden");
        }
        else {
            document.getElementById("VoucherSavingContainer").classList.add("hidden");
        }
    }

    /**
     * Sets the text of an element with the given selector.
     * 
     * Works the same way as the jQuery .text() function in that if an element is not
     * found then it will not produce an error.
     * 
     * Would be useful as a shared utility function across the whole site instead of checking
     * for element existence everywhere.
     * @param selector
     * @param text
     */
    private setText(selector: string, text: string): void {
        let element = document.querySelector(selector);
        if (element) {
            element.textContent = text;
        }
    }

    private WireUpStoreChange(): void {
        if (!this.preventDeliveryRefresh) {
            let dropdown = document.getElementById("DeliveryServices_DeliveryStoreId");

            if (dropdown) {
                dropdown.addEventListener("change", (e) => this.RequestDeliveryUpdate());
            }
        }
    }

    /**
     * Retrieves the totals output from the BasketTotals partial as JSON and attached to the window.
     */
    private get basketTotals(): IBasketTotals {
        return window["BasketTotals"];
    }

    private set basketTotals(basketTotals: IBasketTotals) {
        window["BasketTotals"] = basketTotals;
    }
}
